/**
 * How the console writes a distance: WHOLE METRES, digits grouped, the
 * unit `m` every time -- `13 m`, `240 m`, `5,124 m`, `152,300 m`. This
 * is the ONE place a distance is turned into words: the Tasks tab (live
 * rows, Older tasks, the phone cards, the hover notes), the CSV, and
 * every Metrics tile, chart, table and tooltip read it, so no screen can
 * drift back to kilometres.
 *
 * WHY METRES, AND WHY NEVER SWITCHING TO KM (Akash, 2026-10-21). On the
 * live nav graph a trip is 13-51 m one way (median 29) and a zone clean
 * measures 75-145 m, so a task is about 15-250 m and a whole day about
 * 5,000 m. In kilometres nearly every task would be a `0.0x` decimal,
 * and a unit that changes at a threshold (`999 m`, then `1.0 km`) makes
 * a column of figures impossible to compare by eye. The wheel odometer
 * is good to about 1-2 %, so a whole metre is the honest precision: a
 * decimetre would claim an accuracy the counter does not have.
 *
 * The api-server's Metrics fields stay in kilometres (`distanceKm` and
 * friends, rounded server-side to the metre); converting is this
 * module's job (`metresFromKm`), never the server's.
 */

/** What a distance that was never measured reads: a dash, never `0 m`. */
export const UNKNOWN_DISTANCE = '—';

/** Written in front of a figure that is only a LOWER bound. */
export const AT_LEAST = '≥';

/** Written in front of a figure part of which was shared out over a gap
 *  in readings: the total is known, where it fell is estimated. */
export const ABOUT = '≈';

/** The api-server's kilometres as metres. */
export function metresFromKm(km: number): number {
  return km * 1000;
}

/**
 * The whole number of metres a figure is shown, and exported, as. `-0`
 * comes back as `0`, so a rounding never writes `-0 m`.
 */
export function wholeMetres(metres: number): number {
  return Math.round(metres) || 0;
}

/**
 * `5,124` -- whole metres with a comma between thousands, and no unit:
 * a chart tick, under a heading that names the unit. Grouped by hand,
 * not through `toLocaleString`, for the same reason `date-format.ts`
 * fixes its dates: the browser's locale must not decide whether a hotel
 * manager reads `5,124`, `5.124` or `5 124`. The comma is what en-SG
 * writes. A non-finite number reads as unknown.
 */
export function groupedMetres(metres: number): string {
  if (!Number.isFinite(metres)) return UNKNOWN_DISTANCE;
  const whole = wholeMetres(metres);
  const digits = String(Math.abs(whole)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return whole < 0 ? `-${digits}` : digits;
}

/** `5,124 m`. Measured zero is `0 m`; see `boundedMetres` for unknown. */
export function formatMetres(metres: number): string {
  const grouped = groupedMetres(metres);
  return grouped === UNKNOWN_DISTANCE ? grouped : `${grouped} m`;
}

/**
 * A figure with its mark, or a dash when nothing was measured -- never
 * `0 m` for "no reading". A lower bound wins over an approximation: "some
 * of it is missing" is the stronger thing to know about a number, and a
 * figure that is both is still at least what was measured.
 */
export function boundedMetres(
  metres: number | null | undefined,
  lowerBound = false,
  approximate = false,
): string {
  if (metres === null || metres === undefined || !Number.isFinite(metres)) {
    return UNKNOWN_DISTANCE;
  }
  const text = formatMetres(metres);
  if (lowerBound) return `${AT_LEAST} ${text}`;
  return approximate ? `${ABOUT} ${text}` : text;
}
